import { Component, ElementRef, EventEmitter, Output, Input, ContentChild, TemplateRef, ViewChild } from '@angular/core';
import { trigger, state, style, transition, animate, AnimationEvent } from '@angular/animations';

import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';

let idx: number = 0;

/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the panel has an external error message, or it needs extra description for screen reader
 * + ***canOnlyClickIcon*** - by default both the header and the toggle button are in the clickable area;
 *                            set this to true if only the button should be clickable
 * + ***headingLevel*** - use this if the panel has a different level in application's heading hierarchy from the default
 * + ***toggleArrowTooltip*** - use this if the toggle button needs an explicit tooltip (when the header only has a toggle button and no header)
 */
@Component({
    selector: 'agpr-panel',
    templateUrl: './panel.component.html',
    styleUrls: ['./panel.component.scss'],
    animations: [
        trigger('panelContent', [
            state('hidden', style({
                height: '0',
                opacity: 0
            })),
            state('visible', style({
                height: '*',
                opacity: 1
            })),
            transition('visible <=> hidden', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ],
    standalone: false
})
export class PanelComponent {

    @Input() id: string = `ui-panel-${idx++}`;
    @Input() toggleable: boolean = true;
    @Input() header: string;
    @Input() headerIcon: string;
    @Input() collapsed: boolean = false;
    @Input() contentPadding: boolean = true;
    @Input() style: any;
    @Input() styleClass: string;
    @Input() showHeader: boolean = true;
    @Input() expandIcon: string = 'fa-angle-down';
    @Input() collapseIcon: string = 'fa-angle-up';
    @Input() extraLabel: string;
    @Input() toggleArrowTooltip: string;
    @Input() tooltip: string;
    @Input() headerIconTooltip: string;
    @Input() required: boolean = false;
    @Input() headingLevel: number = 4;
    @Input() isFormGroup: boolean = false;
    @Input() canOnlyClickIcon: boolean = false;
    @Input() headerUppercase: boolean = false;
    @Input() clickExcludeIds: string[] = [];
    @Input() ariaDescribedBy: string;
    @Input() paddingBottom: number = 10;
    @Input() marginTop: number = 10;
    @Input() noBorder?: boolean = false;
    @Input() disableToggle: boolean = false;
    @Input() iconAfter: boolean = false;
    @Input() subHeaderTemplate: TemplateRef<any>;
    @Input() showSubHeader: boolean = false;
    @Input() showCustomHeader: boolean = true;
    @Input() spaceBetweenExpand: boolean = true;

    @ContentChild('panelHeader') public panelHeader: TemplateRef<any>;

    @ViewChild('panelContentWrapper') public panelContentWrapper: ElementRef;

    @Output() collapsedChange: EventEmitter<any> = new EventEmitter();
    @Output() beforeToggle: EventEmitter<any> = new EventEmitter();
    @Output() afterToggle: EventEmitter<any> = new EventEmitter();

    animating: boolean;

    readonly buttonTypes = ButtonTypesEnum;

    constructor(private el: ElementRef) {}

    /**
     * Performs the toggle animation
     */
    toggle(event) {
        if (!this.toggleable || this.disableToggle) {
            return;
        }
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        if (this.clickExcludeIds.indexOf(event?.target?.id) > -1) {
            return;
        }
        if (this.animating) {
            return false;
        }

        if (this.canOnlyClickIcon && !event?.target?.classList?.contains('agpr-panel-titlebar-toggler') && !(event instanceof KeyboardEvent)) {
            return;
        }

        this.animating = true;
        this.beforeToggle.emit({originalEvent: event, collapsed: this.collapsed});

        if (this.toggleable) {
            if (this.collapsed) {
                this.expand(event);
            } else {
                this.collapse(event);
            }
        }
        event?.preventDefault();
    }

    expand(event) {
        this.collapsed = false;
        this.panelContentWrapper.nativeElement.style.display = 'block';
        this.collapsedChange.emit(this.collapsed);
    }

    collapse(event) {
        this.collapsed = true;
        this.collapsedChange.emit(this.collapsed);
    }

    getBlockableElement(): HTMLElement {
        return this.el.nativeElement.children[0];
    }

    onToggleDone(event: AnimationEvent) {
        this.animating = false;
        if (this.collapsed) {
            this.panelContentWrapper.nativeElement.style.display = 'none';
        }
        this.afterToggle.emit({originalEvent: event, collapsed: this.collapsed});
    }
}
